// Store/PatientsTreatedSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../../BaseUrl';

// Get headers with auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'accept': '*/*',
    'Authorization': `Bearer ${token}`
  };
};

// Async thunk to fetch patients treated by the doctor
export const fetchPatientsTreated = createAsyncThunk(
  'patientsTreated/fetchPatientsTreated',
  async (_, { rejectWithValue }) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${BaseUrl.consultationurl}Consultations/PatientsTreated`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching patients treated:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NEW: Async thunk to fetch patient consultations with filters
export const fetchPatientConsultationsHistory = createAsyncThunk(
  'patientsTreated/fetchPatientConsultations',
  async ({ mrn, consultationType = 'out_patient', status = 'Created' }, { rejectWithValue }) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${BaseUrl.consultationurl}Consultations/GetPatientConsultations`,
        {
          headers,
          params: {
            MRN: mrn,
            ConsultationType: consultationType,
            status: status
          }
        }
      );
      
      // Extract consultations from response
      const consultations = response.data?.Consultations || [];
      return {
        consultations,
        isUserAllowed: response.data?.IsUserAllowed || false,
        success: response.data?.Success || false,
        message: response.data?.Message || ''
      };
    } catch (error) {
      console.error('Error fetching patient consultations:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NEW: Async thunk to fetch single consultation details
export const fetchConsultationDetails = createAsyncThunk(
  'patientsTreated/fetchConsultationDetails',
  async (consultationId, { rejectWithValue }) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${BaseUrl.consultationurl}Consultations/${consultationId}`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching consultation details:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  patients: {
    outpatients: [],
    inpatients: [],
    onlinepatients: []
  },
  allPatients: [],
  loading: false,
  error: null,
  success: false,
  // NEW: Patient history state
  patientHistory: {
    consultations: [],
    selectedConsultation: null,
    filters: {
      consultationType: 'out_patient',
      status: 'Created',
      searchTerm: ''
    },
    totalCount: 0,
    isUserAllowed: true
  }
};

const patientsTreatedSlice = createSlice({
  name: 'patientsTreated',
  initialState,
  reducers: {
    clearPatientsTreated: (state) => {
      state.patients = { outpatients: [], inpatients: [], onlinepatients: [] };
      state.allPatients = [];
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    // NEW: Patient history reducers
    clearPatientHistory: (state) => {
      state.patientHistory.consultations = [];
      state.patientHistory.selectedConsultation = null;
      state.patientHistory.totalCount = 0;
    },
    setPatientHistoryFilters: (state, action) => {
      state.patientHistory.filters = {
        ...state.patientHistory.filters,
        ...action.payload
      };
    },
    resetPatientHistoryFilters: (state) => {
      state.patientHistory.filters = {
        consultationType: 'out_patient',
        status: 'Created',
        searchTerm: ''
      };
    },
    setPatientHistorySearchTerm: (state, action) => {
      state.patientHistory.filters.searchTerm = action.payload;
    },
    clearSelectedConsultation: (state) => {
      state.patientHistory.selectedConsultation = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Patients Treated
      .addCase(fetchPatientsTreated.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchPatientsTreated.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.patients = action.payload || { outpatients: [], inpatients: [], onlinepatients: [] };
        // Combine all patients into a single array for easier display
        state.allPatients = [
          ...(state.patients.outpatients || []).map(p => ({ ...p, type: 'Outpatient' })),
          ...(state.patients.inpatients || []).map(p => ({ ...p, type: 'Inpatient' })),
          ...(state.patients.onlinepatients || []).map(p => ({ ...p, type: 'Online' }))
        ];
      })
      .addCase(fetchPatientsTreated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch patients treated';
        state.success = false;
      })
      // NEW: Fetch Patient Consultations History
      .addCase(fetchPatientConsultationsHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchPatientConsultationsHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.patientHistory.consultations = action.payload.consultations;
        state.patientHistory.isUserAllowed = action.payload.isUserAllowed;
        state.patientHistory.totalCount = action.payload.consultations.length;
        state.error = null;
      })
      .addCase(fetchPatientConsultationsHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch patient consultations';
        state.success = false;
        state.patientHistory.consultations = [];
        state.patientHistory.totalCount = 0;
      })
      // NEW: Fetch Consultation Details
      .addCase(fetchConsultationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.patientHistory.selectedConsultation = action.payload;
        state.error = null;
      })
      .addCase(fetchConsultationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch consultation details';
        state.patientHistory.selectedConsultation = null;
      });
  }
});

export const { 
  clearPatientsTreated, 
  clearError,
  clearPatientHistory,
  setPatientHistoryFilters,
  resetPatientHistoryFilters,
  setPatientHistorySearchTerm,
  clearSelectedConsultation
} = patientsTreatedSlice.actions;

export default patientsTreatedSlice.reducer;